import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';

// Types
export interface SessionRecord {
  id: number;
  bookingId: number;
  consumerId: number;
  therapistId: number;
  sessionDate: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  sessionType: 'INTAKE' | 'REGULAR' | 'CRISIS';
  status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  amountPaid: number;
  platformFee: number;
  therapistEarnings: number;
  stripePaymentId?: string;
  hasReceipt: boolean;
  consumerName?: string;
  therapistName?: string;
  notes?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SessionStats {
  userId: number;
  userType: 'CONSUMER' | 'THERAPIST';
  totalSessions: number;
  totalAmountCents: number;
  cancelledSessions: number;
  lastSessionDate?: string;
}

export interface SessionHistoryResponse {
  sessions: SessionRecord[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface SessionHistoryFilters {
  status?: 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';
  therapistId?: number;
  consumerId?: number;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

// Consumer hooks

/**
 * Hook to fetch consumer's session history
 */
export function useConsumerSessions(filters: SessionHistoryFilters = {}) {
  return useQuery({
    queryKey: ['consumer-sessions', filters],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.status) params.append('status', filters.status);
      if (filters.therapistId) params.append('therapistId', filters.therapistId.toString());
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await apiClient.get<SessionHistoryResponse>(
        `/sessions/consumer?${params.toString()}`
      );
      return response.data;
    },
  });
}

/**
 * Hook to fetch consumer's session statistics
 */
export function useConsumerStats() {
  return useQuery({
    queryKey: ['consumer-session-stats'],
    queryFn: async () => {
      const response = await apiClient.get<SessionStats>(
        '/sessions/consumer/stats'
      );
      return response.data;
    },
  });
}

// Therapist hooks

/**
 * Hook to fetch therapist's session history
 */
export function useTherapistSessions(filters: SessionHistoryFilters = {}) {
  return useQuery({
    queryKey: ['therapist-sessions', filters],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.status) params.append('status', filters.status);
      if (filters.consumerId) params.append('consumerId', filters.consumerId.toString());
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await apiClient.get<SessionHistoryResponse>(
        `/sessions/therapist?${params.toString()}`
      );
      return response.data;
    },
  });
}

/**
 * Hook to fetch therapist's session statistics
 */
export function useTherapistStats() {
  return useQuery({
    queryKey: ['therapist-session-stats'],
    queryFn: async () => {
      const response = await apiClient.get<SessionStats>(
        '/sessions/therapist/stats'
      );
      return response.data;
    },
  });
}

// Shared hooks

/**
 * Hook to fetch single session details
 */
export function useSessionDetails(sessionId: number | undefined) {
  return useQuery({
    queryKey: ['session', sessionId],
    queryFn: async () => {
      if (!sessionId) throw new Error('Session ID is required');

      const response = await apiClient.get<SessionRecord>(
        `/sessions/${sessionId}`
      );
      return response.data;
    },
    enabled: !!sessionId,
  });
}

/**
 * Hook to download receipt PDF
 */
export function useDownloadReceipt() {
  return useMutation({
    mutationFn: async (sessionId: number) => {
      const response = await apiClient.get(`/sessions/${sessionId}/receipt`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `theraply-receipt-${sessionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return response.data;
    },
  });
}

/**
 * Hook to refresh session stats (for therapists after completing sessions)
 */
export function useRefreshSessionStats() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // This is a client-side only operation to refresh cached data
      await queryClient.invalidateQueries({ queryKey: ['consumer-session-stats'] });
      await queryClient.invalidateQueries({ queryKey: ['therapist-session-stats'] });
      await queryClient.invalidateQueries({ queryKey: ['consumer-sessions'] });
      await queryClient.invalidateQueries({ queryKey: ['therapist-sessions'] });
    },
  });
}

// Helper functions

/**
 * Format session amount from cents to dollars
 */
export function formatSessionAmount(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Format session date for display
 */
export function formatSessionDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format session time for display
 */
export function formatSessionTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

/**
 * Get session status badge color
 */
export function getSessionStatusColor(status: SessionRecord['status']): string {
  switch (status) {
    case 'COMPLETED':
      return 'green';
    case 'SCHEDULED':
      return 'blue';
    case 'CANCELLED':
      return 'red';
    case 'NO_SHOW':
      return 'yellow';
    default:
      return 'gray';
  }
}

/**
 * Get session type display name
 */
export function getSessionTypeName(type: SessionRecord['sessionType']): string {
  switch (type) {
    case 'INTAKE':
      return 'Initial Intake';
    case 'REGULAR':
      return 'Regular Session';
    case 'CRISIS':
      return 'Crisis Intervention';
    default:
      return type;
  }
}
